import { useMutation, useQuery } from '@tanstack/react-query';
import { Form, TabsProps } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Filter } from '../../../@types/filter';
import { bookingApi } from '../../../apis';
import { Booking, CreateBookingDto, CreateBookingDtoTypeEnum, UpdateBookingDto } from '../../../apis/client-axios';
import ButtonWrap from '../../../components/ButtonWrap';
import InputWrap from '../../../components/InputWrap';
import ModalWrap from '../../../components/ModalWrap';
import SvgAdd from '../../../components/svg/SvgAdd';
import SvgDelete from '../../../components/svg/SvgDelete';
import SvgEdit from '../../../components/svg/SvgEdit';
import TableWrap from '../../../components/TableWrap';
import ToastError from '../../../components/Toast/ToastError';
import ToastSuccess from '../../../components/Toast/ToastSuccess';
import Widget from '../../../components/Widget';
import { BOOKING_MANAGEMENT } from '../../../constants/QueryKey';
import './style.scss';

export const BookingManagement = () => {
  const intl = useIntl();
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [tab, setTab] = useState<CreateBookingDtoTypeEnum | string>('MEETING_ROOM');
  const [selectedRowData, setSelectedRowData] = useState<Booking | null>(null);
  const [filter, setFilter] = useState<Filter>({ page: 1, pageSize: 20, fullTextSearch: '' });
  const [form] = Form.useForm();

  const {
    data: dataBooking,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [BOOKING_MANAGEMENT, tab, filter],
    queryFn: () => {
      if (tab === CreateBookingDtoTypeEnum.MeetingRoom) {
        return bookingApi.bookingControllerGetListMeetingRooms(
          filter.page,
          filter.pageSize,
          undefined,
          filter.fullTextSearch,
          undefined,
        );
      } else {
        return bookingApi.bookingControllerGetListCars(
          filter.page,
          filter.pageSize,
          undefined,
          filter.fullTextSearch,
          undefined,
        );
      }
    },
  });
  //Mutation
  const addMutation = useMutation({
    mutationFn: (payload: CreateBookingDto) => bookingApi.bookingControllerCreateUser(payload),
    onSuccess: () => {
      setOpenAddModal(false);
      refetch();
      form.resetFields();
      ToastSuccess({
        content: intl.formatMessage({ id: 'booking.add.success' }),
      });
    },
    onError: (error: { response: { data: { message: string }; status: number } }) => {
      if (error.response.data.message === 'NAME_HAS_BEEN_CREATE!') {
        if (tab === CreateBookingDtoTypeEnum.MeetingRoom) {
          ToastError({
            content: intl.formatMessage({ id: 'booking.noti.room.beenCreated' }),
          });
        } else {
          ToastError({
            content: intl.formatMessage({ id: 'booking.noti.car.beenCreated' }),
          });
        }
      } else {
        ToastError({
          content: intl.formatMessage({ id: 'booking.fail' }),
        });
      }
    },
  });
  const editMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBookingDto }) =>
      bookingApi.bookingControllerUpdateBooking(id, payload),
    onSuccess: () => {
      setOpenEditModal(false);
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'booking.edit.success' }),
      });
    },
    onError: (error: { response: { data: { message: string }; status: number } }) => {
      if (error.response.data.message === 'NAME_HAS_BEEN_CREATE!') {
        if (tab === CreateBookingDtoTypeEnum.MeetingRoom) {
          ToastError({
            content: intl.formatMessage({ id: 'booking.noti.room.beenCreated' }),
          });
        } else {
          ToastError({
            content: intl.formatMessage({ id: 'booking.noti.car.beenCreated' }),
          });
        }
      } else {
        ToastError({
          content: intl.formatMessage({ id: 'booking.fail' }),
        });
      }
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => bookingApi.bookingControllerDeleteBooking(id),
    onSuccess: () => {
      setOpenDeleteModal(false);
      refetch();
      ToastSuccess({
        content: intl.formatMessage({ id: 'booking.delete.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'booking.fail' }),
      });
    },
  });
  const handleAddBooking = () => {
    if (tab === CreateBookingDtoTypeEnum.MeetingRoom) {
      form
        .validateFields()
        .then((values) => {
          const payload: CreateBookingDto = {
            name: form.getFieldValue('addbookings'),
            type: CreateBookingDtoTypeEnum.MeetingRoom,
          };
          addMutation.mutate(payload);
        })
        .catch((error) => {
          console.error('Validation failed:', error);
        });
    } else {
      form
        .validateFields()
        .then((values) => {
          const payload: CreateBookingDto = {
            name: form.getFieldValue('addbookings'),
            type: CreateBookingDtoTypeEnum.Car,
          };
          addMutation.mutate(payload);
        })
        .catch((error) => {
          console.error('Validation failed:', error);
        });
    }
  };
  const handleEditBooking = () => {
    form
      .validateFields()
      .then((values) => {
        const payload = {
          name: form.getFieldValue('editbookings'),
        };
        editMutation.mutate({ id: selectedRowData?.id as string, payload });
      })
      .catch((error) => {
        console.error('Validation failed:', error);
      });
  };
  const handleDeleteBooking = () => {
    deleteMutation.mutate(selectedRowData?.id as string);
  };

  const items: TabsProps['items'] = [
    {
      key: CreateBookingDtoTypeEnum.MeetingRoom,
      label: intl.formatMessage({ id: 'booking.tab.meetingRoom' }),
    },
    {
      key: CreateBookingDtoTypeEnum.Car,
      label: intl.formatMessage({ id: 'booking.tab.vehicle' }),
    },
  ];
  const columns = [
    {
      title:
        tab === CreateBookingDtoTypeEnum.MeetingRoom
          ? intl.formatMessage({ id: 'booking.room.title' })
          : intl.formatMessage({ id: 'booking.car.title' }),
      dataIndex: 'name',
      key: 'name',
      render: (record) => {
        return (
          <div className="booking_management-content-name">
            <p className="booking_management-content-name-tool">{record}</p>
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: 'option',
      key: 'option',
      render: (record, row) => {
        return (
          <div className="booking_management-content-data-options">
            <ButtonWrap
              className="booking_management-content-button-edit"
              onClick={() => {
                setOpenEditModal(true);
                setSelectedRowData(row);
                form.setFieldValue('editbookings', row.name);
              }}
            >
              <SvgEdit /> {intl.formatMessage({ id: 'booking.button.edit' })}
            </ButtonWrap>
            <ButtonWrap
              className="booking_management-content-button-delete"
              onClick={() => {
                setOpenDeleteModal(true);
                setSelectedRowData(row);
              }}
            >
              <SvgDelete />
              {intl.formatMessage({ id: 'booking.button.delete' })}
            </ButtonWrap>
          </div>
        );
      },
    },
  ];
  return (
    <div className="booking_management">
      <div className="booking_management-content">
        <Widget
          breadcrumbs={[intl.formatMessage({ id: 'booking.title' })]}
          tabProps={{
            items,
            onChange: (key: string) => {
              form.resetFields();
              setTab(key);
              setFilter((prevFilters) => ({
                ...prevFilters,
                page: 1,
              }));
              refetch();
            },
            className: 'booking_management-content-tab',
          }}
          content={
            <TableWrap
              isPagination
              loading={isLoading}
              onSearch={(search) => {
                setFilter((prevFilters) => ({
                  ...prevFilters,
                  page: 1,
                  fullTextSearch: search,
                }));
              }}
              tableProps={{
                dataSource: Array.isArray(dataBooking?.data?.data?.content) ? dataBooking?.data?.data?.content : [],
                columns: columns,
                className: 'booking_management-content-table',
              }}
              inputProps={{
                placeholder:
                  tab === CreateBookingDtoTypeEnum.MeetingRoom
                    ? intl.formatMessage({ id: 'booking.search.room.placeholder' })
                    : intl.formatMessage({ id: 'booking.search.car.placeholder' }),
              }}
              paginationProps={{
                disabled: false,
                current: dataBooking?.data?.data?.page || 1,
                pageSize: dataBooking?.data?.data?.size || 20,
                total: dataBooking?.data?.data?.total || 0,
                onChange: (page, pageSize) => {
                  setFilter((prevFilters) => ({
                    ...prevFilters,
                    page: page,
                    pageSize: pageSize,
                  }));
                },
              }}
              rightTopContent={
                <>
                  <ButtonWrap
                    onClick={() => {
                      setOpenAddModal(true);
                    }}
                    variant="outlined"
                    className="booking_management-content-button-add"
                  >
                    <SvgAdd /> {intl.formatMessage({ id: 'booking.button.add' })}
                  </ButtonWrap>
                </>
              }
            />
          }
          title={intl.formatMessage({ id: 'booking.title' })}
        />
      </div>
      <div className="booking_management-content-modal">
        {openAddModal && (
          <ModalWrap
            open={openAddModal}
            cancelText={intl.formatMessage({ id: 'booking.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'booking.button.add' })}
            onSubmit={handleAddBooking}
            onCancel={() => {
              setOpenAddModal(false);
              form.resetFields();
            }}
            type="upsert"
            title={
              tab === CreateBookingDtoTypeEnum.MeetingRoom
                ? intl.formatMessage({ id: 'booking.modal.add.meetingRoom' })
                : intl.formatMessage({ id: 'booking.modal.add.vehicle' })
            }
            className="booking_management-content-modal-add"
            body={
              <Form form={form} layout="vertical">
                <p className="booking_management-content-title">
                  {tab === CreateBookingDtoTypeEnum.MeetingRoom
                    ? intl.formatMessage({ id: 'booking.room.name' })
                    : intl.formatMessage({ id: 'booking.car.name' })}
                  <span className="label_required">*</span>
                </p>
                <Form.Item
                  name="addbookings"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'common.required' }),
                    },
                    {
                      whitespace: true,
                      message:
                        tab === CreateBookingDtoTypeEnum.MeetingRoom
                          ? intl.formatMessage({ id: 'booking.noti.room.invalid' })
                          : intl.formatMessage({ id: 'booking.noti.car.invalid' }),
                    },
                  ]}
                >
                  <InputWrap
                    className={`booking_management-content-modal-add-input`}
                    placeholder={
                      tab === CreateBookingDtoTypeEnum.MeetingRoom
                        ? intl.formatMessage({ id: 'booking.add.room.placeholder' })
                        : intl.formatMessage({ id: 'booking.add.car.placeholder' })
                    }
                    required={true}
                    maxLength={255}
                  />
                </Form.Item>
              </Form>
            }
          />
        )}
      </div>
      <div className="booking_management-content-modal">
        {openEditModal && (
          <ModalWrap
            open={openEditModal}
            cancelText={intl.formatMessage({ id: 'booking.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'booking.button.edit' })}
            onSubmit={handleEditBooking}
            onCancel={() => {
              setOpenEditModal(false);
              form.resetFields();
            }}
            type="upsert"
            title={
              tab === CreateBookingDtoTypeEnum.MeetingRoom
                ? intl.formatMessage({ id: 'booking.modal.edit.meetingRoom' })
                : intl.formatMessage({ id: 'booking.modal.edit.vehicle' })
            }
            className="booking_management-content-modal-add"
            body={
              <Form form={form} layout="vertical">
                <p className="booking_management-content-title">
                  {tab === CreateBookingDtoTypeEnum.MeetingRoom
                    ? intl.formatMessage({ id: 'booking.room.name' })
                    : intl.formatMessage({ id: 'booking.car.name' })}
                  <span className="label_required">*</span>
                </p>
                <Form.Item
                  name="editbookings"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'common.required' }),
                    },
                    {
                      whitespace: true,
                      message:
                        tab === CreateBookingDtoTypeEnum.MeetingRoom
                          ? intl.formatMessage({ id: 'booking.noti.room.invalid' })
                          : intl.formatMessage({ id: 'booking.noti.car.invalid' }),
                    },
                  ]}
                >
                  <InputWrap
                    className={`booking_management-content-modal-add-input`}
                    placeholder={
                      tab === CreateBookingDtoTypeEnum.MeetingRoom
                        ? intl.formatMessage({ id: 'booking.add.room.placeholder' })
                        : intl.formatMessage({ id: 'booking.add.car.placeholder' })
                    }
                    required={true}
                    maxLength={255}
                  />
                </Form.Item>
              </Form>
            }
          />
        )}
      </div>
      <div className="booking_management-content-modal">
        {openDeleteModal && (
          <ModalWrap
            open={openDeleteModal}
            cancelText={intl.formatMessage({ id: 'booking.modal.cancel' })}
            submitText={intl.formatMessage({ id: 'booking.button.delete' })}
            onSubmit={handleDeleteBooking}
            onCancel={() => setOpenDeleteModal(false)}
            type="upsert"
            title={
              tab === CreateBookingDtoTypeEnum.MeetingRoom
                ? intl.formatMessage({ id: 'booking.modal.delete.meetingRoom' })
                : intl.formatMessage({ id: 'booking.modal.delete.vehicle' })
            }
            className="booking_management-content-modal-delete"
            body={
              <div className="booking_management-content-modal-delete-body">
                <p className="booking_management-content-modal-delete-title">
                  <span className="booking_management-content-name-tool name">{selectedRowData?.name}</span>
                  {intl.formatMessage({ id: 'booking.modal.delete' })}
                </p>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};
